import { Link, Typography } from '@material-ui/core';
import { formatDate } from '@superdispatch/dates';
import { Alert } from '@superdispatch/ui-lab';
import { Inline, Stack } from '@superdispatch/ui';
import { useEffect } from 'react';
import { INSTANT_TRANSFER_ACCESS_LOST_HELP_ARTICLE_URL } from 'shared/modules/superpay/instantTransferAccessLostHelpArticleUrl';
import { LinkAnchor, LinkButton } from 'shared/routing/Links';
import {
  trackInstantTransferRecoveryDismissed,
  trackInstantTransferRecoveryBannerViewed,
  trackInstantTransferRecoveryOrderLinkClicked,
  trackInstantTransferRecoverySettleCTAClicked,
} from './InstantTransferRecoveryAnalytics';

interface InstantTransferNotificationWarningProps {
  notificationContext: {
    status: 'warning';
    load_id: string;
    load_guid: string;
    due_date: string;
  };
  message: string;
  onClose?: () => void;
}

export function InstantTransferNotificationWarning({
  notificationContext,
  message,
  onClose,
}: InstantTransferNotificationWarningProps) {
  const loadId = notificationContext.load_id;
  const loadGuid = notificationContext.load_guid;

  useEffect(() => {
    trackInstantTransferRecoveryBannerViewed({
      banner_state: 'at_risk',
      order_id: loadId,
      orders_count: null,
    });
  }, [loadId]);

  return (
    <Alert
      severity="caution"
      onClose={() => {
        trackInstantTransferRecoveryDismissed({
          banner_state: 'at_risk',
          order_id: loadId,
          orders_count: null,
        });
        onClose?.();
      }}
    >
      <Stack space="xxsmall">
        <Typography variant="body1">{message}</Typography>
        <Typography>
          Instant Transfer was issued for{' '}
          <LinkAnchor
            to={`/loads/${loadGuid}`}
            onClick={() => {
              trackInstantTransferRecoveryOrderLinkClicked({
                banner_state: 'at_risk',
                order_id: loadId,
              });
            }}
          >
            {loadId}
          </LinkAnchor>
          . You're responsible if shipper payment isn't collected.
          <br />{' '}
          {`Settle by ${formatDate(notificationContext.due_date, { variant: 'Date' })} to avoid losing access to Instant Transfer.`}
        </Typography>

        <Inline space="small" verticalAlign="bottom">
          <LinkButton
            size="small"
            variant="neutral"
            to={`/loads/${loadGuid}/instant-transfer-settle?source=banner`}
            onClick={() => {
              trackInstantTransferRecoverySettleCTAClicked({
                banner_state: 'at_risk',
                order_id: loadId,
                orders_count: null,
              });
              onClose?.();
            }}
          >
            Settle Payment
          </LinkButton>
          <Link
            target="_blank"
            rel="noopener noreferrer"
            href={INSTANT_TRANSFER_ACCESS_LOST_HELP_ARTICLE_URL}
            onClick={() => {
              onClose?.();
            }}
          >
            Learn more
          </Link>
        </Inline>
      </Stack>
    </Alert>
  );
}
